import React from 'react';
import { formatCalendarPageDate } from '../../calendar/utils';
import type { CalendarWeekTitleRange } from '../../calendar/types';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

function HeaderStat({ value, label, tone }: { value: string; label: string; tone?: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span
        className={cn(
          'font-[var(--font-display)] text-[19px] font-semibold tracking-[-0.02em]',
          !tone && 'text-[var(--ink-0)]',
        )}
        style={tone ? { color: tone } : undefined}
      >
        {value}
      </span>
      <span className="text-[13px] text-[var(--ink-3)]">{label}</span>
    </span>
  );
}

function HeaderDot() {
  return <span className="mx-3.5 h-[3px] w-[3px] rounded-full bg-[var(--cream-shadow)]" />;
}

export interface CalendarPageEyebrowProps {
  workspaceLabel?: string;
  className?: string;
  /** Overrides default "Today · {workspace}". */
  label?: string;
}

export function CalendarPageEyebrow({ workspaceLabel = 'Workspace', className, label }: CalendarPageEyebrowProps) {
  const text = label ?? `Today · ${workspaceLabel}`;
  return (
    <div className={cn('cal-fade flex min-w-0 items-center gap-2', className)}>
      <Icon kind="sparkle" size={13} stroke={2} color="var(--ai-3)" />
      <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--ai-3)]">
        {text}
      </span>
    </div>
  );
}

export interface CalendarPageTitleBlockProps {
  dayTitle?: string;
  daySubtitle?: string;
  className?: string;
  /** Week view date range — e.g. May 20 – 26 */
  weekRange?: CalendarWeekTitleRange;
  /** Day view stats — the row is hidden when unset. */
  dayStats?: {
    appointments: string;
    captured: string;
    remaining: string;
    needAttention: string;
  };
  /** Week view stats — when set, replaces day stats row. */
  weekStats?: {
    appointments: string;
    projected: string;
    atRisk: string;
    newPatients: string;
  };
  /** Month view stats — when set, replaces day stats row. */
  monthStats?: {
    projected: string;
    mtd: string;
    daysLeft: string;
    newPatients: string;
  };
}

export function CalendarPageTitleBlock({
  dayTitle,
  daySubtitle,
  className,
  weekRange,
  dayStats,
  weekStats,
  monthStats,
}: CalendarPageTitleBlockProps) {
  const today = formatCalendarPageDate();
  const title = dayTitle ?? today.dayTitle;
  const subtitle = daySubtitle ?? today.daySubtitle;

  return (
    <div className={cn('cal-fade min-w-0', className)}>
      <h1 className="m-0 font-[var(--font-display)] text-[clamp(40px,5vw,60px)] font-semibold leading-[0.98] tracking-[-0.04em]">
        {weekRange ? (
          <>
            <span className="cal-spectrum">{weekRange.startMonth} </span>
            <span className="text-[var(--success)]">{weekRange.startDay}</span>
            <span className="font-medium text-[var(--ink-3)]">
              {' – '}
              {weekRange.endMonth ? `${weekRange.endMonth} ` : ''}
              {weekRange.endDay}
            </span>
          </>
        ) : (
          <>
            <span className="cal-spectrum">{title}</span>
            <span className="font-medium text-[var(--ink-3)]">{subtitle}</span>
          </>
        )}
      </h1>
      <div className="mt-4 flex flex-wrap items-center">
        {monthStats ? (
          <>
            <HeaderStat value={monthStats.projected} label="projected" tone="var(--success)" />
            <HeaderDot />
            <HeaderStat value={monthStats.mtd} label="MTD" />
            <HeaderDot />
            <HeaderStat value={monthStats.daysLeft} label="days left" tone="var(--terracotta)" />
            <HeaderDot />
            <HeaderStat value={monthStats.newPatients} label="new patients" />
          </>
        ) : weekStats ? (
          <>
            <HeaderStat value={weekStats.appointments} label="appointments" />
            <HeaderDot />
            <HeaderStat value={weekStats.projected} label="projected" tone="var(--success)" />
            <HeaderDot />
            <HeaderStat value={weekStats.atRisk} label="at risk" tone="var(--terracotta)" />
            <HeaderDot />
            <HeaderStat value={weekStats.newPatients} label="new patients" />
          </>
        ) : dayStats ? (
          <>
            <HeaderStat value={dayStats.appointments} label="appointments" />
            <HeaderDot />
            <HeaderStat value={dayStats.captured} label="captured" tone="var(--success)" />
            <HeaderDot />
            <HeaderStat value={dayStats.remaining} label="remaining" />
            <HeaderDot />
            <HeaderStat value={dayStats.needAttention} label="need attention" tone="var(--terracotta)" />
          </>
        ) : null}
      </div>
    </div>
  );
}

export interface CalendarPageHeaderProps {
  workspaceLabel?: string;
  dayTitle?: string;
  daySubtitle?: string;
  className?: string;
}

/** Eyebrow + title + stats (single column). Prefer `CalendarShellHeader` grid for calendar layout. */
export function CalendarPageHeader({
  workspaceLabel = 'Workspace',
  dayTitle,
  daySubtitle,
  className,
}: CalendarPageHeaderProps) {
  return (
    <div className={cn('cal-fade min-w-0', className)}>
      <CalendarPageEyebrow workspaceLabel={workspaceLabel} className="mb-3" />
      <CalendarPageTitleBlock {...(dayTitle ? { dayTitle } : {})} {...(daySubtitle ? { daySubtitle } : {})} />
    </div>
  );
}
